import React, { createContext, useState, useContext, useEffect, useRef } from 'react';
import axios from 'axios';
import { API_URL } from '../config/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth deve ser usado dentro de AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const logoutTimerRef = useRef(null);
  const SESSION_MS = 30 * 60 * 1000;

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    const sessionExpiresAt = Number(localStorage.getItem('session_expires_at') || 0);
    
    console.log('AuthContext inicializando:', { hasToken: !!token, hasUserData: !!userData });
    
    if (token && userData && sessionExpiresAt && sessionExpiresAt > Date.now()) {
      try {
        const parsedUser = JSON.parse(userData);
        setUser(parsedUser);
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        console.log('Usuário restaurado:', parsedUser);
        const remaining = sessionExpiresAt - Date.now();
        logoutTimerRef.current = setTimeout(() => {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          localStorage.removeItem('session_expires_at');
          delete axios.defaults.headers.common['Authorization'];
          setUser(null);
        }, remaining);
      } catch (error) {
        console.error('Erro ao parsear dados do usuário:', error);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('session_expires_at');
      }
    } else {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('session_expires_at');
    }
    
    console.log('AuthContext: Inicialização concluída, loading: false');
    setLoading(false);
  }, []);

  const login = async (email, senha) => {
    try {
      const safeEmail = typeof email === 'string' ? email.trim().toLowerCase() : email;
      const response = await axios.post(`${API_URL}/api/login`, {
        email: safeEmail,
        senha
      });
      
      const { token, user } = response.data;
      const expiresAt = Date.now() + SESSION_MS;
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
      localStorage.setItem('session_expires_at', String(expiresAt));
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      console.log('AuthContext: Usuário setado após login:', user);
      setUser(user);
      if (logoutTimerRef.current) {
        clearTimeout(logoutTimerRef.current);
      }
      logoutTimerRef.current = setTimeout(() => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('session_expires_at');
        delete axios.defaults.headers.common['Authorization'];
        setUser(null);
      }, SESSION_MS);
      console.log('AuthContext: Login bem-sucedido, retornando success: true');
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Erro ao fazer login'
      };
    }
  };

  const register = async (nome, email, senha) => {
    try {
      const safeEmail = typeof email === 'string' ? email.trim().toLowerCase() : email;
      const response = await axios.post(`${API_URL}/api/register`, {
        nome,
        email: safeEmail,
        senha
      });
      
      const { token, user } = response.data;
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setUser(user);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Erro ao cadastrar usuário'
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('session_expires_at');
    delete axios.defaults.headers.common['Authorization'];
    if (logoutTimerRef.current) {
      clearTimeout(logoutTimerRef.current);
      logoutTimerRef.current = null;
    }
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, login, register, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

